import { memo, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import {
  hexToHsv,
  hsvToHex,
  loadRecentColors,
  PICKER_SWATCHES,
  pushRecentColor,
} from "../lib/color-utils.ts";

/** Custom colour picker: swatch trigger → viewport-clamped popover with
 *  an HSV area, hue slider, hex field, presets and recent colours.
 *  Fires onChange live while dragging. Used for EVERY colour control. */
export const ColorPicker = memo(function ColorPicker({
  value,
  onChange,
  onCommit,
  title,
}: {
  value: string;
  onChange: (hex: string) => void;
  /** Fired once when the popover closes, with the final colour — for
   *  callers where each change is an expensive operation. */
  onCommit?: (hex: string) => void;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [hsv, setHsv] = useState(() => hexToHsv(value));
  const [hexField, setHexField] = useState(value);
  const [recents, setRecents] = useState<string[]>([]);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const svRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);

  const W = 248;
  const H = 322;

  const openPicker = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    const left = Math.max(8, Math.min(r ? r.left : 8, window.innerWidth - W - 8));
    let top = (r ? r.bottom : 0) + 6;
    if (top + H > window.innerHeight - 8) top = Math.max(8, (r ? r.top : H) - H - 6);
    setPos({ left, top });
    setHsv(hexToHsv(value));
    setHexField(value);
    latestHex.current = value;
    setRecents(loadRecentColors());
    setOpen(true);
  };

  // The committed colour comes from a ref, not `hsv` — typing a hex and
  // pressing Enter in the same tick would otherwise commit the value
  // captured before the state update landed.
  const latestHex = useRef(value);
  const close = () => {
    const hex = latestHex.current;
    pushRecentColor(hex);
    setOpen(false);
    onCommit?.(hex);
  };

  // Escape closes the picker (capture phase, so the app's global Escape
  // handling doesn't also fire and clear selections underneath).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hsv]);

  const apply = (h: number, s: number, v: number) => {
    setHsv({ h, s, v });
    const hex = hsvToHex(h, s, v);
    latestHex.current = hex;
    setHexField(hex);
    onChange(hex);
  };

  const svDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = svRef.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    el.setPointerCapture(e.pointerId);
    const move = (ev: { clientX: number; clientY: number }) => {
      const r = el.getBoundingClientRect();
      const s = Math.min(1, Math.max(0, (ev.clientX - r.left) / r.width));
      const v = 1 - Math.min(1, Math.max(0, (ev.clientY - r.top) / r.height));
      apply(hsv.h, s, v);
    };
    move(e);
    const onMove = (ev: PointerEvent) => move(ev);
    const onUp = () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  const hueDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = hueRef.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    el.setPointerCapture(e.pointerId);
    const move = (ev: { clientX: number }) => {
      const r = el.getBoundingClientRect();
      const h = 360 * Math.min(0.999, Math.max(0, (ev.clientX - r.left) / r.width));
      apply(h, hsv.s, hsv.v);
    };
    move(e);
    const onMove = (ev: PointerEvent) => move(ev);
    const onUp = () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  const hueCss = `hsl(${hsv.h}, 100%, 50%)`;

  return (
    <div className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        title={title}
        onClick={() => (open ? close() : openPicker())}
        className="pe-color-trigger"
        style={{ background: value }}
      />
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onPointerDown={(e) => {
              e.stopPropagation();
              close();
            }}
            aria-hidden
          />
          <div
            className="pe-color-pop z-50"
            style={{ left: pos.left, top: pos.top, width: W }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div
              ref={svRef}
              className="pe-color-sv"
              style={{
                background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueCss})`,
              }}
              onPointerDown={svDrag}
            >
              <div
                className="pe-color-knob"
                style={{
                  left: `${hsv.s * 100}%`,
                  top: `${(1 - hsv.v) * 100}%`,
                  background: hsvToHex(hsv.h, hsv.s, hsv.v),
                }}
              />
            </div>
            <div ref={hueRef} className="pe-color-hue" onPointerDown={hueDrag}>
              <div
                className="pe-color-knob"
                style={{ left: `${(hsv.h / 360) * 100}%`, top: "50%", background: hueCss }}
              />
            </div>
            <div className="flex items-center gap-2 px-2 pb-1">
              <span
                className="inline-block h-6 w-6 shrink-0 rounded border border-[color:var(--color-border-strong)]"
                style={{ background: hsvToHex(hsv.h, hsv.s, hsv.v) }}
              />
              <input
                value={hexField}
                onChange={(e) => {
                  setHexField(e.target.value);
                  const m = /^#?([0-9a-f]{6})$/i.exec(e.target.value.trim());
                  if (m) {
                    const hex = `#${m[1]!.toLowerCase()}`;
                    latestHex.current = hex;
                    setHsv(hexToHsv(hex));
                    onChange(hex);
                  }
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter" || e.key === "Escape") close();
                }}
                spellCheck={false}
                className="h-7 w-24 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 font-mono text-xs"
                aria-label="Hex colour"
              />
            </div>
            <div className="pe-color-grid">
              {PICKER_SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  title={c}
                  onClick={() => {
                    latestHex.current = c;
                    setHsv(hexToHsv(c));
                    setHexField(c);
                    onChange(c);
                  }}
                  className="pe-color-swatch"
                  style={{ background: c }}
                />
              ))}
            </div>
            {recents.length > 0 && (
              <>
                <div className="px-2 pt-1 text-[10px] text-[color:var(--color-fg-muted)]">
                  Recent
                </div>
                <div className="pe-color-grid">
                  {recents.map((c) => (
                    <button
                      key={c}
                      type="button"
                      title={c}
                      onClick={() => {
                        setHsv(hexToHsv(c));
                        setHexField(c);
                        onChange(c);
                      }}
                      className="pe-color-swatch"
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
});
